/**
 * WSCompanyInfoCard - 워크스페이스 회사정보 입력 카드 (사용자용)
 * 
 * 워크스페이스 관리자가 회사정보를 직접 입력할 수 있는 카드 컴포넌트입니다.
 * 청구서 발행 등에 사용될 수 있는 기본 정보를 관리합니다.
 */

import React, { useState, useEffect } from 'react';
import { t, Trans } from '@lingui/macro';
import { Building2, User, Mail, Phone, MapPin, Pencil, Loader2, Save, X } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { useToast } from '../../../components/ui/use-toast';
import { Fire } from '../../../services/FireClient';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { My } from '../../../services/My';
import { CompanyInfo, defaultCompanyInfo } from '../../../models/WorkspaceAdmin';
import { IconHash } from '../../components/FigmaIcons';

interface WSCompanyInfoCardProps {
    workspaceId: string;
    onUpdate?: (silent?: boolean) => void;
}

const WSCompanyInfoCard: React.FC<WSCompanyInfoCardProps> = ({ workspaceId, onUpdate }) => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [companyInfo, setCompanyInfo] = useState<CompanyInfo>(defaultCompanyInfo());
    const [editedInfo, setEditedInfo] = useState<CompanyInfo>(defaultCompanyInfo());
    const { toast } = useToast();

    useEffect(() => {
        loadCompanyInfo();
    }, [workspaceId]);

    const loadCompanyInfo = async () => {
        setLoading(true);
        try {
            const wsDoc = await getDoc(doc(Fire.store, 'workspaces', workspaceId));
            if (wsDoc.exists()) {
                const data = wsDoc.data();
                if (data.companyInfo) {
                    setCompanyInfo(data.companyInfo);
                    setEditedInfo(data.companyInfo);
                }
            }
        } catch (e) {
            console.error('Failed to load company info:', e);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const updatedInfo: CompanyInfo = {
                ...editedInfo,
                updatedAt: new Date(),
                updatedBy: My.user?.id,
            };

            await updateDoc(doc(Fire.store, 'workspaces', workspaceId), {
                companyInfo: updatedInfo,
                updatedAt: serverTimestamp(),
            });

            setCompanyInfo(updatedInfo);
            setIsEditing(false);
            toast({ title: t`Company information saved`, duration: 2000 });
            // 저장 성공 시에만 데이터 갱신 (await 없이 백그라운드 실행)
            onUpdate?.(true);
        } catch (e) {
            console.error('Failed to save company info:', e);
            toast({ title: t`Failed to save company information`, variant: 'destructive' });
        } finally {
            setSaving(false);
        }
    };


    const handleCancel = () => {
        setEditedInfo(companyInfo);
        setIsEditing(false);
    };

    const handleStartEdit = () => {
        setEditedInfo({ ...companyInfo });
        setIsEditing(true);
    };

    const hasInfo = Boolean(companyInfo.companyName);

    if (loading) {
        return (
            <Card>
                <CardContent className="flex items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                </CardContent>
            </Card>
        );
    }

    return (
        <Card>
            <CardHeader>
                <div className="flex items-center justify-between">
                    <div>
                        <CardTitle className="text-lg"><Trans>Details</Trans></CardTitle>
                        <CardDescription>
                            <Trans>Used for invoices and official communications</Trans>
                        </CardDescription>
                    </div>
                    {!isEditing && (
                        <Button variant="outline" size="sm" onClick={handleStartEdit}>
                            <Pencil className="w-3.5 h-3.5 mr-2" />
                            <Trans>Edit</Trans>
                        </Button>
                    )}
                </div>
            </CardHeader>

            <CardContent>
                {isEditing ? (
                    // 편집 모드
                    <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label><Trans>Company Name</Trans> *</Label>
                                <Input
                                    value={editedInfo.companyName || ''}
                                    onChange={(e) => setEditedInfo({ ...editedInfo, companyName: e.target.value })}
                                    placeholder={t`e.g. Company Inc.`}
                                    autoComplete="off"
                                    className="bg-muted border-border"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label><Trans>Department</Trans></Label>
                                <Input
                                    value={editedInfo.department || ''}
                                    onChange={(e) => setEditedInfo({ ...editedInfo, department: e.target.value })}
                                    placeholder={t`e.g. Sales Dept.`}
                                    autoComplete="off"
                                    className="bg-muted border-border"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label><Trans>Representative</Trans></Label>
                                <Input
                                    value={editedInfo.representative || ''}
                                    onChange={(e) => setEditedInfo({ ...editedInfo, representative: e.target.value })}
                                    placeholder={t`e.g. John Smith`}
                                    autoComplete="off"
                                    className="bg-muted border-border"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label><Trans>Registration Number</Trans></Label>
                                <Input
                                    value={editedInfo.registrationNumber || ''}
                                    onChange={(e) => setEditedInfo({ ...editedInfo, registrationNumber: e.target.value })}
                                    placeholder={t`e.g. 123-45-67890`}
                                    autoComplete="off"
                                    className="bg-muted border-border"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label><Trans>Email</Trans> *</Label>
                                <Input
                                    type="email"
                                    value={editedInfo.email || ''}
                                    onChange={(e) => setEditedInfo({ ...editedInfo, email: e.target.value })}
                                    placeholder="info@example.com"
                                    autoComplete="off"
                                    className="bg-muted border-border"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label><Trans>Phone</Trans></Label>
                                <Input
                                    value={editedInfo.phone || ''}
                                    onChange={(e) => setEditedInfo({ ...editedInfo, phone: e.target.value })}
                                    placeholder="03-1234-5678"
                                    autoComplete="off"
                                    className="bg-muted border-border"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="space-y-2">
                                <Label><Trans>Postal Code</Trans></Label>
                                <Input
                                    value={editedInfo.postalCode || ''}
                                    onChange={(e) => setEditedInfo({ ...editedInfo, postalCode: e.target.value })}
                                    placeholder="100-0001"
                                    autoComplete="off"
                                    className="bg-muted border-border"
                                />
                            </div>
                            <div className="space-y-2 md:col-span-2">
                                <Label><Trans>Address</Trans></Label>
                                <Input
                                    value={editedInfo.address || ''}
                                    onChange={(e) => setEditedInfo({ ...editedInfo, address: e.target.value })}
                                    placeholder={t`e.g. 123 Main St...`}
                                    autoComplete="off"
                                    className="bg-muted border-border"
                                />
                            </div>
                        </div>
                    </div>
                ) : hasInfo ? (
                    // 정보 표시 모드
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-4">
                            <InfoRow
                                icon={<Building2 className="w-4 h-4" />}
                                label={t`Company Name`}
                                value={companyInfo.companyName}
                            />
                            {companyInfo.department && (
                                <InfoRow
                                    icon={<Building2 className="w-4 h-4" />}
                                    label={t`Department`}
                                    value={companyInfo.department}
                                />
                            )}
                            <InfoRow
                                icon={<User className="w-4 h-4" />}
                                label={t`Representative`}
                                value={companyInfo.representative}
                            />
                            <InfoRow
                                icon={<Mail className="w-4 h-4" />}
                                label={t`Email`}
                                value={companyInfo.email}
                            />
                        </div>
                        <div className="space-y-4">
                            <InfoRow
                                icon={<Phone className="w-4 h-4" />}
                                label={t`Phone`}
                                value={companyInfo.phone}
                            />
                            <InfoRow
                                icon={<MapPin className="w-4 h-4" />}
                                label={t`Address`}
                                value={companyInfo.postalCode
                                    ? `〒${companyInfo.postalCode} ${companyInfo.address || ''}`
                                    : companyInfo.address
                                }
                            />
                            <InfoRow
                                icon={<IconHash className="w-4 h-4" />}
                                label={t`Registration Number`}
                                value={companyInfo.registrationNumber}
                                mono
                            />
                        </div>
                    </div>
                ) : (
                    // 정보 없음
                    <div className="text-center py-8 text-muted-foreground">
                        <Building2 className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
                        <p className="text-sm">
                            <Trans>No company information registered.</Trans>
                        </p>
                        <p className="text-xs mt-1">
                            <Trans>Click Edit to add your company details.</Trans>
                        </p>
                    </div>
                )}
            </CardContent>

            {isEditing && (
                <CardFooter className="border-t px-6 py-4 bg-muted/50 flex justify-end gap-2">
                    <Button variant="outline" onClick={handleCancel} disabled={saving}>
                        <X className="w-4 h-4 mr-2" />
                        <Trans>Cancel</Trans>
                    </Button>
                    <Button
                        onClick={handleSave}
                        disabled={saving || !editedInfo.companyName || !editedInfo.email}
                    >
                        {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                        <Save className="w-4 h-4 mr-2" />
                        <Trans>Save</Trans>
                    </Button>
                </CardFooter>
            )}
        </Card>
    );
};

// 정보 표시용 행 컴포넌트
const InfoRow: React.FC<{
    icon: React.ReactNode;
    label: string;
    value?: string;
    mono?: boolean;
}> = ({ icon, label, value, mono }) => (
    <div className="flex items-start gap-3">
        <div className="text-muted-foreground mt-0.5">{icon}</div>
        <div className="flex-1 min-w-0">
            <div className="text-xs text-muted-foreground">{label}</div>
            <div className={`font-medium text-foreground truncate ${mono ? 'font-mono text-sm' : ''}`}>
                {value || '-'}
            </div>
        </div>
    </div>
);

export default WSCompanyInfoCard;
